import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StatusChip from "../general/StatusChip";
import {
  AcquisitionCustomerQcRow,
  AcquisitionRunOutput,
  AcquisitionUploadResult,
  displayAcquisitionRequestId,
} from "models/acquisition-import";
import { useAcquisitionRun } from "src/hooks/acquisition-import/useAcquisitionRun";
import { getNetSuiteLink } from "src/util/helpers";
import {
  formatAcquisitionListDate,
  shouldShowDecisionChip,
} from "./AcquisitionRecordRow";

function decisionColor(
  decision?: string
): "success" | "error" | "warning" | "info" | "default" {
  if (decision === "SUCCESS") return "success";
  if (decision === "FAILED") return "error";
  if (decision === "IN_PROGRESS" || decision === "QUEUED") return "info";
  return "default";
}

function qcRowPassed(row: AcquisitionCustomerQcRow): boolean {
  if (row.qc === "PASS") {
    return true;
  }
  return (
    row.qc === "ALREADY_EXISTS" &&
    !(row.mismatchedFields && row.mismatchedFields.length > 0)
  );
}

function qcColor(
  row: AcquisitionCustomerQcRow
): "success" | "error" | "warning" | "info" | "default" {
  return qcRowPassed(row) ? "success" : "error";
}

function qcRowBackground(row: AcquisitionCustomerQcRow): string {
  return qcRowPassed(row) ? "#e8f5e9" : "#ffebee";
}

function splitNsIds(value?: string): string[] {
  return String(value || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

const NetSuiteRecordIds: React.FC<{
  value?: string;
  recordType:
    | "Customer"
    | "BillingAccount"
    | "Item"
    | "Subscription"
    | "SubscriptionPlan"
    | "PricePlan"
    | "PriceBook";
}> = ({ value, recordType }) => {
  const ids = splitNsIds(value);
  if (ids.length === 0) {
    return <>-</>;
  }
  return (
    <>
      {ids.map((id, index) => (
        <React.Fragment key={id + "-" + String(index)}>
          {index > 0 ? ", " : null}
          <a
            href={getNetSuiteLink(recordType, id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {id}
          </a>
        </React.Fragment>
      ))}
    </>
  );
};

function spreadsheetTabUrl(url?: string, gid?: number): string | undefined {
  if (!url) {
    return undefined;
  }
  if (gid == null) {
    return url;
  }
  const base = url.replace(/[#?].*$/, "");
  return base + "#gid=" + String(gid);
}

function errorLinesFromOutput(
  output: AcquisitionRunOutput,
  failed: boolean
): string[] {
  if (output.validationErrors && output.validationErrors.length > 0) {
    return output.validationErrors;
  }
  if (!failed) {
    return [];
  }
  const reasoning = String(output.reasoning || "").trim();
  if (!reasoning) {
    return [];
  }
  return reasoning
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

const ErrorLines: React.FC<{ lines: string[] }> = ({ lines }) => (
  <Box
    component="ul"
    sx={{ mt: 0.5, mb: 0, pl: 2.5, maxHeight: 320, overflow: "auto" }}
  >
    {lines.map((line, index) => (
      <li key={index}>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {line}
        </Typography>
      </li>
    ))}
  </Box>
);

function deriveUploadResult(
  output: AcquisitionRunOutput
): AcquisitionUploadResult | undefined {
  if (output.uploadResult) {
    return output.uploadResult;
  }
  const customers = output.customers || [];
  if (customers.length === 0) {
    return undefined;
  }
  const missing = customers.filter((row) => row.qc === "MISSING").length;
  const duplicates = customers.filter((row) => row.qc === "DUPLICATE").length;
  const uploaded = customers.filter(
    (row) => row.qc === "PASS" || row.qc === "FIELD_MISMATCH"
  ).length;
  if (missing === 0 && duplicates === 0) {
    return "SUCCESS";
  }
  if (uploaded > 0) {
    return "PARTIAL";
  }
  return "FAILED";
}

const CompactTable: React.FC<{
  headers: string[];
  children: React.ReactNode;
  minWidth?: number;
}> = ({ headers, children, minWidth }) => (
  <TableContainer sx={{ maxHeight: 360, overflowX: "auto" }}>
    <Table
      size="small"
      stickyHeader
      sx={minWidth ? { minWidth } : undefined}
    >
      <TableHead>
        <TableRow>
          {headers.map((header) => (
            <TableCell key={header} sx={{ whiteSpace: "nowrap" }}>
              {header}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  </TableContainer>
);

const AcquisitionImportRun: React.FC = () => {
  const { acquisitionId = "", runId = "" } = useParams<{
    acquisitionId: string;
    runId: string;
  }>();
  const navigate = useNavigate();
  const { run, loading, refetch } = useAcquisitionRun(acquisitionId, runId);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRecord = useCallback(
    async (silent = false) => {
      setRefreshing(true);
      try {
        await refetch(silent);
      } finally {
        setRefreshing(false);
      }
    },
    [refetch]
  );

  useEffect(() => {
    if (run?.status === "QUEUED" || run?.status === "IN_PROGRESS") {
      const intervalId = setInterval(() => fetchRecord(true), 5000);
      return () => clearInterval(intervalId);
    }
  }, [run?.status, fetchRecord]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!run) {
    return (
      <Box p={3}>
        <Typography>Stage run not found.</Typography>
      </Box>
    );
  }

  const output = run.output;
  const isItems = run.input.stageCode === "items";
  const isSubPlan = run.input.stageCode === "sub_plan";
  const isPricePlan = run.input.stageCode === "price_plans";
  const isPriceBook = run.input.stageCode === "price_books";
  const isBilling = run.input.stageCode === "billing_accounts";
  const isSubscriptions = run.input.stageCode === "subscriptions";
  const isPendingActivation =
    run.input.stageCode === "pending_activation_lines";
  const nsRecordType:
    | "Customer"
    | "BillingAccount"
    | "Item"
    | "Subscription"
    | "SubscriptionPlan"
    | "PricePlan"
    | "PriceBook" = isPriceBook
    ? "PriceBook"
    : isPricePlan
    ? "PricePlan"
    : isSubPlan
      ? "SubscriptionPlan"
      : isItems
        ? "Item"
        : isBilling
          ? "BillingAccount"
          : isSubscriptions || isPendingActivation
            ? "Subscription"
            : "Customer";
  const inputRows = output.inputRows || [];
  const customers = output.customers || [];
  const notUploaded =
    output.notUploaded ||
    customers
      .filter((row) => row.qc === "MISSING")
      .map((row) => ({
        externalId: row.externalId,
        reason:
          row.mismatch ||
          `Record was not uploaded: External ID "${row.externalId}" was not found in NetSuite.`,
      }));
  const alreadyExists =
    output.alreadyExists ||
    customers
      .filter((row) => row.qc === "ALREADY_EXISTS")
      .map((row) => ({
        externalId: row.externalId,
        reason:
          row.mismatch ||
          `External ID "${row.externalId}" already exists in NetSuite and was not uploaded in this run.`,
      }));
  const nsIdByExternalId = new Map(
    customers
      .filter((row) => Boolean(row.nsId))
      .map((row) => [row.externalId, row.nsId as string])
  );
  const mismatches = customers.filter(
    (row) =>
      row.qc === "FIELD_MISMATCH" ||
      (Boolean(row.mismatchedFields) && (row.mismatchedFields || []).length > 0)
  );
  const duplicates = customers.filter((row) => row.qc === "DUPLICATE");
  const uploadResult = deriveUploadResult(output);
  const showDecisionChip = shouldShowDecisionChip(
    run.status,
    output.decision
  );
  const isPlaceholderOutput =
    (run.status === "QUEUED" || run.status === "IN_PROGRESS") &&
    (output.decision === "PENDING" ||
      output.decision === "IN_PROGRESS" ||
      !output.decision) &&
    !(output.customers && output.customers.length > 0) &&
    !(output.inputRows && output.inputRows.length > 0) &&
    !output.taskId;
  const failedRun =
    run.status === "FAILED" || output.decision === "FAILED";
  const errorLines = errorLinesFromOutput(output, failedRun);
  const inputTabUrl = spreadsheetTabUrl(
    output.workingSpreadsheetUrl,
    output.customerSheetGid
  );
  const outputTabUrl = spreadsheetTabUrl(
    output.netsuiteSpreadsheetUrl,
    output.netsuiteSheetGid
  );

  const stageLabel = run.input.stageLabel || "Stage";
  let alertSeverity: "success" | "warning" | "error" | "info" | undefined;
  let alertTitle = "";
  if (run.status === "FAILED" || output.decision === "FAILED") {
    alertSeverity = "error";
    alertTitle =
      uploadResult === "SUCCESS"
        ? stageLabel + " failed after upload QC"
        : stageLabel + " failed";
  } else if (uploadResult === "SUCCESS" && mismatches.length === 0 && alreadyExists.length === 0) {
    alertSeverity = "success";
    alertTitle = stageLabel + " was successful";
  } else if (uploadResult === "SUCCESS" && mismatches.length > 0) {
    alertSeverity = "warning";
    alertTitle =
      alreadyExists.length > 0
        ? stageLabel + " was successful, with skips and field mismatches"
        : stageLabel + " was successful, with field mismatches";
  } else if (uploadResult === "SUCCESS" && alreadyExists.length > 0) {
    alertSeverity = "info";
    alertTitle =
      (output.uploadedCount || 0) > 0
        ? stageLabel + " was successful. Some external IDs already exist"
        : "No new records were uploaded";
  } else if (uploadResult === "PARTIAL") {
    alertSeverity = "warning";
    alertTitle = stageLabel + " was not fully successful";
  } else if (uploadResult === "FAILED") {
    alertSeverity = "error";
    alertTitle = stageLabel + " failed";
  }

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3} gap={2}>
          <IconButton
            onClick={() => navigate(`/acquisition-imports/${acquisitionId}`)}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4">
              {run.acquisitionName || displayAcquisitionRequestId(run.acquisitionId)}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {run.input.stageLabel} · {displayAcquisitionRequestId(run.runId)}
            </Typography>
          </Box>
          <StatusChip status={run.status} />
          {showDecisionChip && output.decision ? (
            <Chip
              label={output.decision}
              color={decisionColor(output.decision)}
            />
          ) : null}
          {refreshing && <CircularProgress size={24} />}
        </Box>

        {isPlaceholderOutput && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This run is still {run.status === "QUEUED" ? "queued" : "in progress"}.
            DynamoDB has not written a SUCCESS or FAILED result yet, so the
            output below is the processing placeholder.
          </Alert>
        )}

        {alertSeverity && (
          <Alert severity={alertSeverity} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">{alertTitle}</Typography>
            {errorLines.length > 0 && (
              <ErrorLines lines={errorLines} />
            )}
            {alreadyExists.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2">
                  {alreadyExists.length} external ID(s) already exist in
                  NetSuite and were not uploaded in this run:
                </Typography>
                <ul style={{ margin: "4px 0 0 16px" }}>
                  {alreadyExists.map((row) => (
                    <li key={row.externalId}>
                      {row.companyName
                        ? `${row.externalId} (${row.companyName})`
                        : row.externalId}
                      {nsIdByExternalId.get(row.externalId) ? (
                        <>
                          {" "}
                          (NS ID:{" "}
                          <NetSuiteRecordIds
                            recordType={nsRecordType}
                            value={nsIdByExternalId.get(row.externalId)}
                          />
                          )
                        </>
                      ) : null}
                      {": "}
                      {row.reason}
                    </li>
                  ))}
                </ul>
              </Box>
            )}
            {notUploaded.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2">
                  {notUploaded.length} record(s) were not uploaded:
                </Typography>
                <ul style={{ margin: "4px 0 0 16px" }}>
                  {notUploaded.map((row) => (
                    <li key={row.externalId}>
                      {row.companyName
                        ? `${row.externalId} (${row.companyName})`
                        : row.externalId}
                      {": "}
                      {row.reason}
                    </li>
                  ))}
                </ul>
              </Box>
            )}
            {duplicates.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2">
                  {duplicates.length} record(s) matched more than one NetSuite
                  record:
                </Typography>
                <ul style={{ margin: "4px 0 0 16px" }}>
                  {duplicates.map((row: AcquisitionCustomerQcRow) => (
                    <li key={row.externalId}>
                      {row.externalId}
                      {row.nsId ? (
                        <>
                          {" "}
                          (NS ID:{" "}
                          <NetSuiteRecordIds
                            recordType={nsRecordType}
                            value={row.nsId}
                          />
                          )
                        </>
                      ) : null}
                      {row.mismatch ? `: ${row.mismatch}` : ""}
                    </li>
                  ))}
                </ul>
              </Box>
            )}
            {mismatches.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2">
                  Detailed QC field mismatches:
                </Typography>
                <ul style={{ margin: "4px 0 0 16px" }}>
                  {mismatches.map((row) => (
                    <li key={row.externalId}>
                      {row.externalId}
                      {row.nsId ? (
                        <>
                          {" "}
                          (NS ID:{" "}
                          <NetSuiteRecordIds
                            recordType={nsRecordType}
                            value={row.nsId}
                          />
                          )
                        </>
                      ) : null}
                      {row.mismatchedFields && row.mismatchedFields.length > 0
                        ? ` — ${row.mismatchedFields.join(", ")}`
                        : ""}
                      {row.mismatch ? `: ${row.mismatch}` : ""}
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </Alert>
        )}

        <Box mb={3}>
          <Typography variant="h6">Input</Typography>
          <Typography variant="body2">
            <strong>Request ID:</strong> {displayAcquisitionRequestId(run.runId)}
          </Typography>
          <Typography variant="body2">
            <strong>Acquisition ID:</strong>{" "}
            {displayAcquisitionRequestId(run.acquisitionId)}
          </Typography>
          <Typography variant="body2">
            <strong>Stage:</strong> {run.input.stageLabel} ({run.input.stageCode})
          </Typography>
          {run.input.stageCode === "price_plans" ||
          run.input.stageCode === "price_books" ? (
            <>
              <Typography variant="body2">
                <strong>P_FIN_2 spreadsheet:</strong>{" "}
                <a href={run.input.spreadsheetUrl} target="_blank" rel="noreferrer">
                  {run.input.spreadsheetUrl}
                </a>
              </Typography>
              {run.input.subscriptionPlanId ? (
                <Typography variant="body2">
                  <strong>Subscription Plan ID:</strong>{" "}
                  {run.input.subscriptionPlanId}
                </Typography>
              ) : null}
            </>
          ) : run.input.stageCode === "pending_activation_lines" ? (
            <>
              <Typography variant="body2">
                <strong>P_FIN_2 spreadsheet:</strong>{" "}
                <a
                  href={
                    run.input.fin8SpreadsheetUrl || run.input.spreadsheetUrl
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {run.input.fin8SpreadsheetUrl || run.input.spreadsheetUrl}
                </a>
              </Typography>
              {run.input.fin28SpreadsheetUrl ||
              run.input.fin27SpreadsheetUrl ? (
                <Typography variant="body2">
                  <strong>P_FIN_4 spreadsheet:</strong>{" "}
                  <a
                    href={
                      run.input.fin28SpreadsheetUrl ||
                      run.input.fin27SpreadsheetUrl
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {run.input.fin28SpreadsheetUrl ||
                      run.input.fin27SpreadsheetUrl}
                  </a>
                </Typography>
              ) : null}
            </>
          ) : run.input.stageCode === "subscriptions" ||
            run.input.stageCode === "contractual_docs" ||
            run.input.stageCode === "link_contractual_docs" ? (
            <>
              <Typography variant="body2">
                <strong>P_FIN_2 spreadsheet:</strong>{" "}
                <a
                  href={
                    run.input.fin8SpreadsheetUrl || run.input.spreadsheetUrl
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {run.input.fin8SpreadsheetUrl || run.input.spreadsheetUrl}
                </a>
              </Typography>
              {run.input.fin27SpreadsheetUrl ? (
                <Typography variant="body2">
                  <strong>P_FIN_4 spreadsheet:</strong>{" "}
                  <a
                    href={run.input.fin27SpreadsheetUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {run.input.fin27SpreadsheetUrl}
                  </a>
                </Typography>
              ) : null}
              {run.input.subscriptionPlanId ? (
                <Typography variant="body2">
                  <strong>Subscription Plan ID:</strong>{" "}
                  {run.input.subscriptionPlanId}
                </Typography>
              ) : null}
              {run.input.productName ? (
                <Typography variant="body2">
                  <strong>Product Name:</strong> {run.input.productName}
                </Typography>
              ) : null}
              {run.input.classFullname ? (
                <Typography variant="body2">
                  <strong>Class:</strong> {run.input.classFullname}
                </Typography>
              ) : null}
              {run.input.departmentFullname ? (
                <Typography variant="body2">
                  <strong>Department:</strong> {run.input.departmentFullname}
                </Typography>
              ) : null}
            </>
          ) : run.input.stageCode === "billing_accounts" ? (
            <>
              <Typography variant="body2">
                <strong>P_FIN_2 spreadsheet:</strong>{" "}
                <a
                  href={
                    run.input.fin8SpreadsheetUrl || run.input.spreadsheetUrl
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {run.input.fin8SpreadsheetUrl || run.input.spreadsheetUrl}
                </a>
              </Typography>
              {run.input.fin28SpreadsheetUrl ? (
                <Typography variant="body2">
                  <strong>P_FIN_4 spreadsheet:</strong>{" "}
                  <a
                    href={run.input.fin28SpreadsheetUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {run.input.fin28SpreadsheetUrl}
                  </a>
                </Typography>
              ) : null}
              {run.input.classFullname ? (
                <Typography variant="body2">
                  <strong>Class:</strong> {run.input.classFullname}
                </Typography>
              ) : null}
              {run.input.departmentFullname ? (
                <Typography variant="body2">
                  <strong>Department:</strong> {run.input.departmentFullname}
                </Typography>
              ) : null}
              {run.input.invoiceForm ? (
                <Typography variant="body2">
                  <strong>Invoice Form:</strong> {run.input.invoiceForm}
                </Typography>
              ) : null}
            </>
          ) : (
            <Typography variant="body2">
              <strong>Source spreadsheet:</strong>{" "}
              <a href={run.input.spreadsheetUrl} target="_blank" rel="noreferrer">
                {run.input.spreadsheetUrl}
              </a>
            </Typography>
          )}
          {run.input.jiraTicket ? (
            <Typography variant="body2">
              <strong>Jira Ticket:</strong> {run.input.jiraTicket}
            </Typography>
          ) : null}
          {run.input.comment ? (
            <Typography variant="body2">
              <strong>Comment:</strong> {run.input.comment}
            </Typography>
          ) : null}
          {inputTabUrl && (
            <Typography variant="body2">
              <strong>Working input tab:</strong>{" "}
              <a href={inputTabUrl} target="_blank" rel="noreferrer">
                {output.customerSheetName || "Open input tab"}
              </a>
              {output.customerSheetGid != null
                ? ` (gid=${output.customerSheetGid})`
                : ""}
            </Typography>
          )}
          {(run.input.requesterName || run.input.requesterEmail) && (
            <Typography variant="body2">
              <strong>Requestor:</strong>{" "}
              {run.input.requesterName || run.input.requesterEmail}
              {run.input.requesterEmail && run.input.requesterName
                ? ` (${run.input.requesterEmail})`
                : ""}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>Queued:</strong> {formatAcquisitionListDate(run.createdAt)}
          </Typography>
          {inputRows.length > 0 && (
            <Accordion sx={{ mt: 1 }} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">
                  Compact input rows ({inputRows.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CompactTable
                  minWidth={isPriceBook || isPricePlan ? 960 : undefined}
                  headers={
                    isPriceBook
                      ? [
                          "Sub Plan ID",
                          "Price Book (External ID)",
                          "Name",
                          "Currency",
                          "Line",
                          "Repeat Every",
                          "Charge Frequency",
                          "Price Plan External ID",
                        ]
                      : isPricePlan
                      ? [
                          "Currency",
                          "External ID",
                          "Type",
                          "Starting Quantity",
                          "Pricing Option",
                          "Value",
                        ]
                      : isSubPlan
                        ? [
                            "External ID",
                            "Item External ID",
                            "Type",
                            "Sub Plan ID",
                            "Plan Name",
                          ]
                        : [
                            "External ID",
                            "Company Name",
                            "Invoice Email",
                            "Inquiry Email",
                            "Subsidiary",
                            "Currency",
                          ]
                  }
                >
                  {inputRows.map((row, index) => (
                    <TableRow key={row.externalId + "-" + String(index)}>
                      {isPriceBook ? (
                        <>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.subPlanId || row.subsidiary || "-"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.externalId}
                          </TableCell>
                          <TableCell>{row.companyName || "-"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.currency || "-"}
                          </TableCell>
                          <TableCell>{row.planLineNumber || "-"}</TableCell>
                          <TableCell>{row.repeatEvery || "-"}</TableCell>
                          <TableCell>{row.chargeFrequency || "-"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.pricePlanExternalId || "-"}
                          </TableCell>
                        </>
                      ) : isPricePlan ? (
                        <>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.currency || "-"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.externalId}
                          </TableCell>
                          <TableCell>{row.pricePlanType || row.companyName || "-"}</TableCell>
                          <TableCell>
                            {row.startingQuantity || row.inquiryEmail || "-"}
                          </TableCell>
                          <TableCell>
                            {row.pricingOption || row.invoiceEmail || "-"}
                          </TableCell>
                          <TableCell>{row.tierValue || "0"}</TableCell>
                        </>
                      ) : isSubPlan ? (
                        <>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.planExternalId || row.invoiceEmail || "-"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.itemExternalId || row.externalId}
                          </TableCell>
                          <TableCell>{row.inquiryEmail || "-"}</TableCell>
                          <TableCell>{row.subPlanId || row.subsidiary || "-"}</TableCell>
                          <TableCell>{row.companyName || "-"}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.externalId}
                          </TableCell>
                          <TableCell>{row.companyName || "-"}</TableCell>
                          <TableCell>{row.invoiceEmail || "-"}</TableCell>
                          <TableCell>{row.inquiryEmail || "-"}</TableCell>
                          <TableCell>{row.subsidiary || "-"}</TableCell>
                          <TableCell>{row.currency || "-"}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </CompactTable>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>

        <Box>
          <Typography variant="h6">Output</Typography>
          <Typography variant="body2">
            <strong>Decision:</strong> {output.decision || "-"}
          </Typography>
          {output.validationErrors && output.validationErrors.length > 0 ? (
            <Typography variant="body2">
              <strong>Summary:</strong> {output.validationErrors.length}{" "}
              {output.decision === "SUCCESS" || run.status === "SUCCESS"
                ? "warning"
                : "validation error"}
              {output.validationErrors.length === 1 ? "" : "s"} listed below.
            </Typography>
          ) : errorLines.length > 0 ? (
            <>
              <Typography variant="body2">
                <strong>Summary:</strong>
              </Typography>
              <ErrorLines lines={errorLines} />
            </>
          ) : output.reasoning ? (
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              <strong>Summary:</strong> {output.reasoning}
            </Typography>
          ) : null}
          {output.createdCount != null && (
            <Typography variant="body2">
              <strong>Rows written:</strong> {output.createdCount}
            </Typography>
          )}
          {output.missingPrerequisites &&
            output.missingPrerequisites.length > 0 && (
              <Typography variant="body2">
                <strong>Missing:</strong>{" "}
                {output.missingPrerequisites.join("; ")}
              </Typography>
            )}
          {output.uploadedCount != null && (
            <Typography variant="body2">
              <strong>New records uploaded:</strong> {output.uploadedCount}
            </Typography>
          )}
          {alreadyExists.length > 0 && (
            <Typography variant="body2">
              <strong>Already existing (skipped):</strong> {alreadyExists.length}
            </Typography>
          )}
          {output.taskId && (
            <Typography variant="body2">
              <strong>CSV import task:</strong> {output.taskId}
              {output.csvImportStatus ? ` (${output.csvImportStatus})` : ""}
            </Typography>
          )}
          {outputTabUrl && (
            <Typography variant="body2">
              <strong>Working NetSuite tab:</strong>{" "}
              <a href={outputTabUrl} target="_blank" rel="noreferrer">
                {output.netsuiteSheetName || "Open NetSuite tab"}
              </a>
              {output.netsuiteSheetGid != null
                ? ` (gid=${output.netsuiteSheetGid})`
                : ""}
            </Typography>
          )}
          {output.validationErrors && output.validationErrors.length > 0 && (
            <Box mt={1}>
              <Typography variant="body2">
                <strong>
                  {output.decision === "SUCCESS" || run.status === "SUCCESS"
                    ? "Warnings:"
                    : "Validation errors:"}
                </strong>
              </Typography>
              <Box sx={{ color: "error.main" }}>
                <ErrorLines lines={output.validationErrors} />
              </Box>
            </Box>
          )}
          {customers.length > 0 && (
            <Accordion sx={{ mt: 1 }} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">
                  Compact QC output ({customers.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CompactTable
                  minWidth={isPriceBook || isPricePlan ? 1200 : undefined}
                  headers={
                    isPriceBook
                      ? [
                          "Price Book (External ID)",
                          "NS ID",
                          "Name",
                          "Currency",
                          "Line",
                          "Repeat Every",
                          "Charge Frequency",
                          "Price Plan External ID",
                          "QC",
                          "Mismatched fields",
                        ]
                      : isPricePlan
                      ? [
                          "External ID",
                          "NS ID",
                          "Currency",
                          "Type",
                          "Starting Quantity",
                          "Pricing Option",
                          "Value",
                          "QC",
                          "Mismatched fields",
                        ]
                      : isSubPlan
                        ? [
                            "External ID",
                            "NS ID",
                            "Item External ID",
                            "Sub Plan ID",
                            "Plan Name",
                            "QC",
                            "Mismatched fields",
                          ]
                        : isItems
                          ? [
                              "External ID",
                              "NS ID",
                              "Item Name",
                              "Subsidiary",
                              "Class",
                              "Department",
                              "QC",
                              "Mismatched fields",
                            ]
                          : isSubscriptions
                            ? [
                                "External ID",
                                "NS ID",
                                "Sub Name",
                                "Customer",
                                "Plan ID",
                                "Currency",
                                "QC",
                                "Mismatched fields",
                              ]
                          : [
                              "External ID",
                              "NS ID",
                              "Name",
                              "Subsidiary",
                              "Currency",
                              "Email",
                              "QC",
                              "Mismatched fields",
                            ]
                  }
                >
                  {customers.map((row) => (
                    <TableRow
                      key={
                        (row.itemExternalId || row.externalId) +
                        "-" +
                        (row.planLineNumber || "") +
                        "-" +
                        (row.nsId || "")
                      }
                      sx={{ backgroundColor: qcRowBackground(row) }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {isSubPlan
                          ? row.planExternalId || row.entityid || row.externalId
                          : row.externalId}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <NetSuiteRecordIds
                          recordType={nsRecordType}
                          value={row.nsId}
                        />
                      </TableCell>
                      {isPriceBook ? (
                        <>
                          <TableCell>{row.fullname || row.altname || "-"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.currency || "-"}
                          </TableCell>
                          <TableCell>{row.planLineNumber || "-"}</TableCell>
                          <TableCell>{row.repeatEvery || "-"}</TableCell>
                          <TableCell>{row.chargeFrequency || "-"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.pricePlanExternalId || "-"}
                          </TableCell>
                        </>
                      ) : isPricePlan ? (
                        <>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.currency || "-"}
                          </TableCell>
                          <TableCell>{row.pricePlanType || "-"}</TableCell>
                          <TableCell>{row.startingQuantity || "-"}</TableCell>
                          <TableCell>{row.pricingOption || "-"}</TableCell>
                          <TableCell>{row.tierValue || "-"}</TableCell>
                        </>
                      ) : isSubPlan ? (
                        <>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.itemExternalId || row.externalId}
                          </TableCell>
                          <TableCell>{row.subPlanId || row.nsId || "-"}</TableCell>
                          <TableCell>{row.fullname || "-"}</TableCell>
                        </>
                      ) : isSubscriptions ? (
                        <>
                          <TableCell>{row.fullname || row.altname || "-"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.subsidiary || "-"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.subPlanId || "-"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.currency || "-"}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            {row.altname || row.fullname || "-"}
                          </TableCell>
                          <TableCell>{row.subsidiary || "-"}</TableCell>
                          {isItems ? (
                            <>
                              <TableCell>{row.classFullname || "-"}</TableCell>
                              <TableCell>
                                {row.departmentFullname || "-"}
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>{row.currency || "-"}</TableCell>
                              <TableCell>{row.email || "-"}</TableCell>
                            </>
                          )}
                        </>
                      )}
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.qc}
                          color={qcColor(row)}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxWidth: 360,
                        }}
                      >
                        {row.mismatchedFields && row.mismatchedFields.length > 0
                          ? row.mismatchedFields.join(", ")
                          : ""}
                        {row.mismatch
                          ? (row.mismatchedFields &&
                            row.mismatchedFields.length > 0
                              ? ": "
                              : "") + row.mismatch
                          : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </CompactTable>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AcquisitionImportRun;
